import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import GoogleMapEmbed from '../components/GoogleMapEmbed';

const Contact: React.FC = () => {
  const form = useRef<HTMLFormElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Send email using EmailJS
    if (form.current) {
      emailjs
        .sendForm('service_p1x0tve', 'template_id5b1tn', form.current, {
          publicKey: 'MWcQGLU2AE_igmg5t',
        })
        .then(() => {
          console.log('SUCCESS!');
        })
        .catch((error) => {
          console.log('FAILED...', error.text);
        });
    }

    // Show confirmation and reset
    console.log('Form submitted:', formData);
    setFormSubmitted(true);

    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });

    setTimeout(() => {
      setFormSubmitted(false);
    }, 5000);
  };

  return (
    <div className="bg-primary-50">
      {/* Hero Section */}
      <section className="relative bg-spice-orange text-white py-20">
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-primary-100">
              Have questions about our products or services? We're here to help. Get in touch with our team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Contact Info */}
            <motion.div
  initial={{ opacity: 0, x: -30 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.6 }}
>
  <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
  <p className="text-lg text-primary-600 mb-8">
    We'd love to hear from you. Whether you have a question about our products, sourcing, or anything else, our team is ready to answer all your questions.
  </p>

  <div className="space-y-6">
    <div className="flex items-center mb-4">
      <div className="bg-primary-100 rounded-full p-3 mr-4">
        {/* phone icon */}
        <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold">Phone</h3>
        <a href="tel:+919687903220" className="text-gray-600 hover:text-primary-600">+91 9687903220</a>
      </div>
    </div>

    <div className="flex items-center mb-4">
      <div className="bg-primary-100 rounded-full p-3 mr-4">
        {/* email icon */}
        <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold">Email</h3>
        <a href="mailto:dd.exim08@gmail.com" className="text-gray-600 hover:text-primary-600">dd.exim08@gmail.com</a>
      </div>
    </div>

    <div className="flex items-center">
      <div className="bg-primary-100 rounded-full p-3 mr-4">
        {/* location icon */}
        <svg className="h-6 w-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </div>
      <div>
        <h3 className="text-lg font-semibold">Address</h3>
        <a href="https://maps.app.goo.gl/YHKGHP6UvomaW2Wr9" target="_blank" rel="noopener noreferrer" className="text-gray-600 not-italic">
          501, Shlok Heights, 8- Kayaji Plot<br />
          B/h Sardar Bag,Morbi-363641<br />
          Gujarat,India<br />
        </a>
      </div>
    </div>
  </div>
</motion.div>


            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 rounded-lg shadow-md"
            >
              <h2 className="text-2xl font-bold mb-6">Send Us a Message</h2>

              {formSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6"
                >
                  <p>Thank you for your message! We'll get back to you as soon as possible.</p>
                </motion.div>
              )}

              <form ref={form} onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-primary-700 font-medium mb-2">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-spice-red"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="email" className="block text-primary-700 font-medium mb-2">Your Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-spice-red"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="subject" className="block text-primary-700 font-medium mb-2">Subject</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-spice-red"
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="Product Inquiry">Product Inquiry</option>
                    <option value="Wholesale Information">Wholesale Information</option>
                    <option value="Sourcing Information">Sourcing Information</option>
                    <option value="General Question">General Question</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-primary-700 font-medium mb-2">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full px-4 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-spice-red"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-full py-3"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <h2 className="text-2xl font-bold mb-8 text-center">Visit Our Headquarters</h2>
          <div className="rounded-lg overflow-hidden shadow-md h-96">
            <div className="w-full h-full bg-primary-200 flex items-center justify-center">
              <GoogleMapEmbed />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
